import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../utils/axiosConfig';
import '../styles/JobBoardSettings.css';

interface JobBoard {
  id: string;
  name: string;
  displayName: string;
  isActive: boolean;
  isPremium: boolean;
  lastSyncAt: string | null;
}

interface BoardPreference {
  id?: string;
  boardName: string;
  isEnabled: boolean;
  priority: number;
}

interface BoardStats {
  name: string;
  displayName: string;
  isActive: boolean;
  isPremium: boolean;
  lastSyncAt: string | null;
  jobCount: number;
  status: string;
}

const JobBoardSettings: React.FC = () => {
  const [boards, setBoards] = useState<JobBoard[]>([]);
  const [preferences, setPreferences] = useState<BoardPreference[]>([]);
  const [stats, setStats] = useState<BoardStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [boardsRes, prefsRes, statsRes] = await Promise.all([
        axiosInstance.get('/auth/job-boards'),
        axiosInstance.get('/auth/job-boards/preferences'),
        axiosInstance.get('/auth/jobs/board-stats')
      ]);

      const boardsData = boardsRes.data;
      const prefsData = prefsRes.data;
      const statsData = statsRes.data;

      setBoards(boardsData.boards || []);
      setPreferences(prefsData.preferences || []);
      setStats(statsData.stats || []);
    } catch (err) {
      setError('Failed to load job board settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleBoard = (boardName: string) => {
    setPreferences(prev => {
      const existing = prev.find(p => p.boardName === boardName);
      if (existing) {
        return prev.map(p =>
          p.boardName === boardName ? { ...p, isEnabled: !p.isEnabled } : p
        );
      } else {
        return [...prev, { boardName, isEnabled: true, priority: prev.length + 1 }];
      }
    });
    setSuccessMsg(null);
  };

  const handlePriorityChange = (boardName: string, direction: 'up' | 'down') => {
    setPreferences(prev => {
      const sorted = [...prev].sort((a, b) => a.priority - b.priority);
      const index = sorted.findIndex(p => p.boardName === boardName);
      
      if (direction === 'up' && index > 0) {
        const newPriority = sorted[index - 1].priority;
        sorted[index - 1].priority = sorted[index].priority;
        sorted[index].priority = newPriority;
      } else if (direction === 'down' && index < sorted.length - 1) {
        const newPriority = sorted[index + 1].priority;
        sorted[index + 1].priority = sorted[index].priority;
        sorted[index].priority = newPriority;
      }
      
      return sorted;
    });
    setSuccessMsg(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const response = await axiosInstance.put('/auth/job-boards/preferences', { preferences });

      const data = response.data;
      setPreferences(data.preferences);
      setSuccessMsg('Preferences saved successfully!');
    } catch (err) {
      setError('Failed to save job board preferences');
    } finally {
      setSaving(false);
    }
  };

  const getBoardPreference = (boardName: string): BoardPreference | undefined => {
    return preferences.find(p => p.boardName === boardName);
  };

  const getBoardStats = (boardName: string): BoardStats | undefined => {
    return stats.find(s => s.name === boardName);
  };

  const formatLastSync = (date: string | null): string => {
    if (!date) return 'Never';
    const d = new Date(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="jbs-container">
        <div className="jbs-loading">Loading job board settings...</div>
      </div>
    );
  }

  return (
    <div className="jbs-container">
      <div className="jbs-header">
        <button className="jbs-back-btn" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1>Job Board Settings</h1>
        <p>Configure which job boards to search and their priority order</p>
      </div>

      {error && <div className="jbs-error">{error}</div>}
      {successMsg && <div className="jbs-success">{successMsg}</div>}

      <div className="jbs-boards-section">
        <h2>Available Job Boards</h2>
        <p className="jbs-info">
          Enable the job boards you want to search. Higher priority boards are searched first.
        </p>

        <div className="jbs-boards-list">
          {boards.map(board => {
            const pref = getBoardPreference(board.name);
            const boardStats = getBoardStats(board.name);
            const isEnabled = pref?.isEnabled ?? false;

            return (
              <div key={board.id} className={`jbs-board-card ${isEnabled ? 'enabled' : ''}`}>
                <div className="jbs-board-header">
                  <div className="jbs-board-info">
                    <span className="jbs-board-name">{board.displayName}</span>
                    {board.isPremium && <span className="jbs-premium-badge">Premium</span>}
                  </div>
                  <label className="jbs-toggle">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={() => handleToggleBoard(board.name)}
                      disabled={board.isPremium && !board.isActive}
                    />
                    <span className="jbs-toggle-slider"></span>
                  </label>
                </div>

                <div className="jbs-board-details">
                  <div className="jbs-stat">
                    <span className="jbs-stat-label">Status:</span>
                    <span className={`jbs-stat-value ${board.isActive ? 'active' : 'inactive'}`}>
                      {board.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="jbs-stat">
                    <span className="jbs-stat-label">Cached Jobs:</span>
                    <span className="jbs-stat-value">{boardStats?.jobCount ?? 0}</span>
                  </div>
                  <div className="jbs-stat">
                    <span className="jbs-stat-label">Last Sync:</span>
                    <span className="jbs-stat-value">{formatLastSync(board.lastSyncAt)}</span>
                  </div>
                </div>

                {isEnabled && pref && (
                  <div className="jbs-priority-controls">
                    <span className="jbs-priority-label">Priority: {pref.priority}</span>
                    <div className="jbs-priority-buttons">
                      <button
                        onClick={() => handlePriorityChange(board.name, 'up')}
                        disabled={pref.priority === 1}
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => handlePriorityChange(board.name, 'down')}
                        disabled={pref.priority === preferences.filter(p => p.isEnabled).length}
                        title="Move down"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                )}

                {board.isPremium && !board.isActive && (
                  <div className="jbs-premium-notice">
                    Upgrade to Premium to access this job board
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="jbs-actions">
        <button className="jbs-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>

      <div className="jbs-tips">
        <h3>Tips</h3>
        <ul>
          <li>Enable multiple boards to see more job listings</li>
          <li>Jobs are automatically deduplicated across boards</li>
          <li>Higher priority boards' results appear first in unified search</li>
          <li>Premium boards offer access to exclusive job listings</li>
        </ul>
      </div>
    </div>
  );
};

export default JobBoardSettings;
